import React, {
  Children,
  cloneElement,
  PropsWithChildren,
  ReactElement,
  useMemo,
  forwardRef,
} from "react";

import { AvatarGroupsStyles, LengthCountStyles } from "./Styles";
import { AvatarProps } from "../Avatar/Avatar";
import { Tooltip } from "../Tooltip";
import { Typography } from "../Typography/Typography";

export interface AvatarGroupsProps {
  limit: number;
  size?: number;
  hasTooltip?: boolean;
}

export const AvatarGroup = forwardRef<
  HTMLDivElement,
  PropsWithChildren<AvatarGroupsProps>
>(({ limit, size = 34, children, hasTooltip = false, ...rest }, ref) => {
  // always display counter with min +2 as +1 can takes space of an 1 more Avatar
  const minChildren = Children.count(children) - limit > 1 ? false : true;
  // gap between Avatars
  const gap = 8;

  const _children = minChildren
    ? Children.toArray(children)
    : Children.toArray(children).slice(0, limit);

  const handleVariant = useMemo(() => {
    if (size >= 34) return "Caption 1";
    return "Caption 2";
  }, [size]);

  // get all names indexed above limit number from alt tag on Avatar for counter tooltip
  const nameArr = Children.toArray(children)
    ?.slice(limit)
    .map((item) => {
      const { props } = item as ReactElement;
      return props.alt;
    });

  return (
    <AvatarGroupsStyles hasTooltip={hasTooltip} ref={ref} {...rest}>
      {_children.map((child, index) => {
        const item = child as ReactElement<AvatarProps>;
        const tooltipText = item.props.alt;
        const alt = `Image of ${tooltipText}`;
        return (
          <Tooltip
            title={tooltipText ? tooltipText : ""}
            disable={!hasTooltip}
            key={alt}
          >
            {cloneElement(item, {
              ...item.props,
              size: size,
              alt: alt,
              style: {
                // component preserves it's width
                marginLeft: `-${index === 0 ? null : gap}px`,
                outline: "1px solid var(--page-paper-main)",
              },
            })}
          </Tooltip>
        );
      })}

      {limit >= Children.count(children) || minChildren ? null : (
        <Tooltip
          title={nameArr.join(",\n")}
          disable={!hasTooltip}
          css={{
            whiteSpace: "break-spaces",
            lineHeight: "18px",
          }}
        >
          <LengthCountStyles
            size={size}
            role="status"
            aria-label="Rest of the users"
            css={{
              marginLeft: `-${gap}px`,
            }}
          >
            <Typography variant={handleVariant} weight="medium">
              +{Children.count(children) - limit}
            </Typography>
          </LengthCountStyles>
        </Tooltip>
      )}
    </AvatarGroupsStyles>
  );
});

AvatarGroup.displayName = "AvatarGroup";
